import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { TelegramAuthData } from '@telegram-auth/react';
import { authAPI, userAPI } from '../services/api';
import { User, AuthContextType, AuthProviderProps } from '../types';
import { extractAuthToken } from '../utils/telegram';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  // token 失效时（拦截器派发），清空登录态，使 TelegramAutoLogin 可重新触发自动登录
  useEffect(() => {
    const handleUnauthorized = () => setUser(null);
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, []);

  const fetchUser = async () => {
    try {
      const response = await userAPI.getUserInfo();
      setUser(response.data.data);
    } catch (error: any) {
      console.error('Failed to fetch user info:', error.response?.status, error.response?.data);
      // Remove token on 401 (unauthorized) or 403 (forbidden)
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('token');
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const checkLogin = async () => {
    try {
      const response = await userAPI.checkLogin();
      return response.data.is_login;
    } catch (error: any) {
      console.error('Check login failed:', error.response?.status, error.response?.data);
      return false;
    }
  };

  const persistAuthToken = async (responseData: { auth_data?: string; token?: string }) => {
    const token = extractAuthToken(responseData);
    localStorage.setItem('token', token);
    await fetchUser();
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await authAPI.login(email, password);
      await persistAuthToken(response.data.data);
    } catch (error: any) {
      console.error('Login failed:', error.response?.status, error.response?.data);
      throw error;
    }
  };

  const register = async (
    email: string,
    password: string,
    email_code?: string,
    invite_code?: string,
    recaptcha_data?: string,
    telegram_id?: number,
    init_data?: string
  ) => {
    const response = await authAPI.register(
      email,
      password,
      email_code,
      invite_code,
      recaptcha_data,
    );
    await persistAuthToken(response.data.data);
  };

  const telegramLogin = useCallback(async (telegramUser: any, initData: string) => {
    try {
      const response = await authAPI.telegramLogin(
        telegramUser.id,
        initData,
      );
      await persistAuthToken(response.data.data);
    } catch (error: any) {
      console.error('Telegram login failed:', error.response?.status, error.response?.data);
      throw error;
    }
  }, []);

  const telegramWidgetLogin = async (telegramUser: TelegramAuthData) => {
    try {
      const response = await authAPI.telegramWidgetLogin(telegramUser);
      await persistAuthToken(response.data.data);
    } catch (error: any) {
      console.error('Telegram widget login failed:', error.response?.status, error.response?.data);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...userData });
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    checkLogin,
    telegramLogin,
    telegramWidgetLogin,
    logout,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};